import {
  ArmyListUnit,
  ArmyListUnitOption,
  ArmyOrganisation,
  ConstraintScope,
  ConstraintType,
  IConstrainedObject,
  Nullable,
  Unit,
  UnitOptionAvailabilityAction,
  UnitOptionCategory,
  UnitOptionUsePoint,
} from '@Models';
import { AppState } from '@Redux/store';
import { Result } from '@Redux/type';
import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { ArmyListUnitUnitOptionAction } from './army-list-unit-unit-option.action';
import { ArmyListUnitUnitOptionChangeProfilAction } from './army-list-unit-unit-option-change-profil.action';
import { OptionView } from '@Selectors/builder/myArmyList/make/unit/edit/OptionView';
import { calculArmyListUnitMaxSize } from '@Selectors/builder/myArmyList/make/calculArmyListUnitMaxSize';
import armyListUnitSlice from '@Redux/reducers/builder/armyListUnit.slice';
import {
  getActiveArmyListUnits,
  getArmy,
  getArmyList,
  getArmyListUnit,
  getUnits,
} from '@Selectors/builder/myArmyList/make/unit/edit/selector/editSelector';
import { UnitOptionGroupView } from '@Selectors/builder/myArmyList/make/view/unit-option-group.view';

@injectable()
export class ArmyListUnitOptionAction {
  @inject(TYPES.Actions.ArmyListUnitUnitOptionAction)
  private _armyListUnitUnitOptionAction!: ArmyListUnitUnitOptionAction;
  @inject(TYPES.Actions.ArmyListUnitUnitOptionChangeProfilAction)
  private _changeProfilAction!: ArmyListUnitUnitOptionChangeProfilAction;

  public computeArmyListUnitPoints = (
    armyListUnit: ArmyListUnit,
    unit: Unit,
    armyOrganisation: Nullable<ArmyOrganisation>,
  ): number => {
    let points = unit.points;
    if (unit.add_value_points > 0) {
      points +=
        ((armyListUnit.size ?? unit.min_size) - unit.min_size) *
        unit.add_value_points;
    }

    points += armyListUnit.army_list_unit_unit_options
      .filter((option) => !option.isDeleted)
      .map((option) => option.points)
      .reduce((a, b) => a + b, 0);

    return points;
  };

  public updateArmyListUnitPoints = (
    state: AppState,
    armyListUnit: ArmyListUnit,
  ): ArmyListUnit => {
    const unit = state.army.entity?.units?.find(
      (unit) => unit.id === armyListUnit.unit_id,
    );
    if (!unit) return armyListUnit;

    const armyOrganisation =
      state.army.entity?.army_organisations?.find(
        (item) => item.id === state.armyList.entity?.army_organisation_id,
      ) ?? null;

    armyListUnit.points = this.computeArmyListUnitPoints(
      armyListUnit,
      unit,
      armyOrganisation,
    );
    return armyListUnit;
  };

  public actionArmyListUnitOptionSelect =
    (
      allOptions: Array<OptionView>,
      unitOptionGroup: UnitOptionGroupView,
      options: Array<OptionView>,
      item: OptionView,
      isChecked: boolean,
    ): Result<void> =>
    async (dispatch, getState) => {
      const armyListUnit = getArmyListUnit(getState());
      if (!armyListUnit) throw new Error('armyListUnit not found');

      const unitId = item.unitId ?? armyListUnit.unit_id;
      const units = getUnits(getState());
      const unit = units.find((item) => item.id === unitId);
      if (!unit) throw new Error('unit not found');

      const army = getArmy(getState());
      if (!army) throw new Error('army not found');

      const armyList = getArmyList(getState());
      if (!armyList) throw new Error('armyList not found');

      const armyListUnits = getActiveArmyListUnits(getState());

      const armyOrganisation = army.army_organisations.find(
        (org) => org.id === armyList.army_organisation_id,
      );
      if (!armyOrganisation) throw new Error('armyOrganisation not found');

      let armyListUnitUpdated = {
        ...armyListUnit,
        army_list_unit_unit_options:
          this._armyListUnitUnitOptionAction.updateArmyListUnitUnitOptions(
            armyListUnit,
            unit,
            item.id,
          ),
        carac: {
          ...armyListUnit.carac,
        },
        troops: armyListUnit.troops.map((t) => ({ ...t })),
      };

      if (isChecked) {
        const modifiers =
          unit.optionGroups
            .flatMap((o) => o.options)
            .find((o) => o.id === item.id)?.availabilityModifiers ?? [];
        if (modifiers.length > 0) {
          // Auto selection des options par une autre options
          modifiers
            .filter((o) => o.action === UnitOptionAvailabilityAction.AutoSelect)
            .forEach((o) => {
              const modifiedOption = allOptions.find(
                (opt) => opt.id === o.unitOptionId,
              );

              if (modifiedOption) {
                dispatch(
                  this.actionArmyListUnitOptionSelect(
                    allOptions,
                    unitOptionGroup,
                    options,
                    modifiedOption,
                    true,
                  ),
                );
              }
            });

          // Mise à jour des prix pour points d'une autre option quand l'option est sélectionnée
          modifiers
            .filter(
              (o) => o.action === UnitOptionAvailabilityAction.ChangePoints,
            )
            .forEach((o) => {
              armyListUnitUpdated.army_list_unit_unit_options =
                armyListUnitUpdated.army_list_unit_unit_options.map(
                  (option) => {
                    if (option.unit_option_id != o.unitOptionId) return option;

                    if (o.usePoints === UnitOptionUsePoint.Cost) {
                      return {
                        ...option,
                        points: option.quantity * o.value!!,
                      } satisfies ArmyListUnitOption as ArmyListUnitOption;
                    }

                    return {
                      ...option,
                      points: 0,
                    } satisfies ArmyListUnitOption as ArmyListUnitOption;
                  },
                );
            });
        }

        armyListUnitUpdated = this.limitChildSelection(
          armyListUnitUpdated,
          item,
          options,
        );
        armyListUnitUpdated = this.limitChildSelectionByOptionGroup(
          unit.optionGroups.find((og) => og.id === unitOptionGroup.id)!!,
          armyListUnitUpdated,
          item,
          options,
        );

        armyListUnitUpdated.army_list_unit_unit_options =
          this._armyListUnitUnitOptionAction.addArmyListUnitUnitOptions(
            armyListUnitUpdated.army_list_unit_unit_options,
            item.id,
            item.name,
            item.quantity!!,
            item.points!!.current,
          );
      } else {
        // On désactive les options enfants si le parent est déselectionné
        this.findChildOptions(item.id, options).forEach((option) => {
          armyListUnitUpdated.army_list_unit_unit_options =
            this._armyListUnitUnitOptionAction.removeArmyListUnitUnitOption(
              armyListUnitUpdated.army_list_unit_unit_options,
              option.id,
            );
        });
      }

      // on affecte la monture
      const mountOption = unit.optionGroups
        .flatMap((o) => o.options)
        .filter((o) => o.category === UnitOptionCategory.Mount)
        .find((o) => o.id === item.id);
      if (mountOption) {
        armyListUnitUpdated.mountId = mountOption.mount_id;
        armyListUnitUpdated.mount =
          units.find((o) => o.id === mountOption.mount_id) ?? null;
      }

      armyListUnitUpdated = this._changeProfilAction.updateUnitProfil(
        armyListUnitUpdated,
        unit,
      );

      armyListUnitUpdated.size = Math.min(
        armyListUnitUpdated.size,
        calculArmyListUnitMaxSize(
          unit,
          armyListUnitUpdated,
          armyOrganisation,
          armyListUnits,
          armyListUnitUpdated.army_list_unit_unit_options.filter(
            (o) => !o.isDeleted,
          ),
          armyList.size,
        ),
      );

      dispatch(
        armyListUnitSlice.actions.setArmyListUnit(
          this.updateArmyListUnitPoints(getState(), armyListUnitUpdated),
        ),
      );
    };

  findChildOptions(
    parentId: number | string,
    options: Array<OptionView>,
  ): Array<OptionView> {
    const childs = options.filter((option) => option.parentId === parentId);

    if (childs.length === 0) return [];

    return [
      ...childs,
      ...childs.map((option) => this.findChildOptions(option.id, options)),
    ].flat();
  }

  public actionArmyListUnitOptionQuantityChange =
    (
      unitOptionGroup: UnitOptionGroupView,
      options: Array<OptionView>,
      item: OptionView,
      input: string,
      min: number,
      max: number,
    ): Result<void> =>
    async (dispatch, getState) => {
      let armyListUnit = getState().armyListUnit.entity;
      if (!armyListUnit) throw new Error('armyListUnit not found');

      let quantity = this.parseQuantity(input);
      if (max != null && quantity > max) {
        quantity = max;
      }
      if (quantity < 0) {
        quantity = 1;
      }

      dispatch(
        armyListUnitSlice.actions.setArmyListUnit(
          this.updateArmyListUnitPoints(getState(), {
            ...armyListUnit,
            army_list_unit_unit_options:
              this._armyListUnitUnitOptionAction.updateArmyListUnitUnitOptionQuantity(
                armyListUnit.army_list_unit_unit_options,
                item,
                quantity,
              ),
          }),
        ),
      );
    };

  limitChildSelection(
    armyListUnitUpdated: ArmyListUnit,
    item: OptionView,
    options: Array<OptionView>,
  ): ArmyListUnit {
    const parentOption = options.find((o) => o.id === item.parentId);
    if (!item.parentId || !parentOption || !parentOption.maxSelectedChilds) {
      return armyListUnitUpdated;
    }

    const sameParentOptions = options.filter(
      (o) => o.parentId === item.parentId,
    );

    return this.dropOptionToMoveUnderLimitSelection(
      item.id,
      parentOption.maxSelectedChilds -
        (item.isPerModel ? (item.quantity ?? 1) : 1),
      sameParentOptions,
      options,
      armyListUnitUpdated,
    );
  }

  limitChildSelectionByOptionGroup(
    unitOptionGroup: IConstrainedObject,
    armyListUnitUpdated: ArmyListUnit,
    item: OptionView,
    options: Array<OptionView>,
  ): ArmyListUnit {
    const constraint = unitOptionGroup.constraints.find(
      (c) =>
        c.type === ConstraintType.Max && c.scope === ConstraintScope.Parent,
    );
    if (!constraint) {
      return armyListUnitUpdated;
    }

    const parentIds = this.findParentOptionIds(options, item.parentId);
    const sameParentOptions = options.filter(
      (o) => !o.parentId && !parentIds.includes(o.id),
    );

    return this.dropOptionToMoveUnderLimitSelection(
      item.id,
      constraint.value - (item.isPerModel ? (item.quantity ?? 1) : 1),
      sameParentOptions,
      options,
      armyListUnitUpdated,
    );
  }

  private findParentOptionIds(
    options: Array<OptionView>,
    parentId: number | null,
  ): number[] {
    if (!parentId) return [];

    const parentOption = options.find((o) => o.id === parentId);
    if (!parentOption) return [];

    return [
      parentId,
      ...this.findParentOptionIds(options, parentOption.parentId),
    ];
  }

  private dropOptionToMoveUnderLimitSelection(
    optionId: number,
    maxSelectedChilds: number,
    sameParentOptions: OptionView[],
    allOptions: OptionView[],
    armyListUnit: ArmyListUnit,
  ) {
    const sameParentActiveOptions = sameParentOptions.filter((o) =>
      armyListUnit.army_list_unit_unit_options.some(
        (uo) =>
          uo.unit_option_id === o.id &&
          !uo.isDeleted &&
          uo.unit_option_id !== optionId,
      ),
    );

    while (
      maxSelectedChilds <
      sameParentActiveOptions.reduce(
        (a, b) => a + (b.isPerModel ? (b.quantity ?? 1) : 1) * b.weight,
        0,
      )
    ) {
      const itemId = sameParentActiveOptions.shift()?.id;

      if (!itemId) break;

      [
        itemId,
        ...this.findChildOptions(itemId, allOptions).map((o) => o.id),
      ].forEach((optionId) => {
        armyListUnit.army_list_unit_unit_options =
          this._armyListUnitUnitOptionAction.removeArmyListUnitUnitOption(
            armyListUnit.army_list_unit_unit_options,
            optionId,
          );
      });
    }

    return armyListUnit;
  }

  private parseQuantity(quantity: string): number {
    const parsed = parseInt(quantity);
    if (isNaN(parsed)) {
      return 0;
    }
    return parsed;
  }
}
